import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import LocationAutocomplete from "../components/LocationAutocomplete";
import { Calendar, MapPin, Wallet, ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";
import ShareButtons from "../components/ShareButtons";
import { Link } from "react-router-dom";

export default function CreateEvent() {
const { user } = useAuth();
  const nav = useNavigate();
  const { eventId } = useParams();
  const isEdit = !!eventId;
  const [form, setForm] = useState({
    name: "", date: "", venue: "", location: "", lat: null, lng: null,
    budget: "", description: "", is_public: false,
  });
  const [loading, setLoading] = useState(false);
  const [createdEvent, setCreatedEvent] = useState(null);
  const [incompletePopup, setIncompletePopup] = useState(false);
  const [loadingEvent, setLoadingEvent] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/events/${eventId}`).then(({ data: ev }) => {
      setForm({
        name: ev.name || "",
        date: ev.date ? ev.date.split("T")[0] : "",
        venue: ev.venue || "",
        location: ev.location || "",
        lat: ev.lat || null,
        lng: ev.lng || null,
        budget: ev.budget != null ? String(ev.budget) : "",
        description: ev.description || "",
        is_public: !!ev.is_public,
      });
      setLoadingEvent(false);
    }).catch(() => { toast.error("Failed to load event"); setLoadingEvent(false); });
    // eslint-disable-next-line
  }, [eventId]);  

  if (!user || user.role === "worker") {
    return <div className="p-12 text-center"><p className="font-display text-2xl">Access denied</p></div>;
  }
  if (isEdit && loadingEvent) {
    return <div className="p-12 text-center text-muted-foreground">Loading event…</div>;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!user.company_name || !user.company_name.trim()) {
      setIncompletePopup(true);
      return;
    }
    if (!form.name.trim()) { toast.error("Event name is required"); return; }
    if (!form.date) { toast.error("Event date is required"); return; }
    if (!form.venue.trim()) { toast.error("Venue is required"); return; }
    setLoading(true);
    try {
      const payload = { ...form, budget: form.budget ? parseFloat(form.budget) : null };
      if (isEdit) {
        await api.patch(`/events/${eventId}`, payload);
        toast.success("Event updated!");
        nav(`/manage-event/${eventId}`);
      } else {
        const { data } = await api.post("/events", payload);
        setCreatedEvent(data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEdit ? "Failed to update event" : "Failed to create event"));
    } finally { setLoading(false); }
  };

  if (incompletePopup) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4">
        <div className="bg-card rounded-2xl p-6 max-w-sm w-full text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h3 className="font-display text-xl mb-2">Complete your profile first</h3>
          <p className="text-sm text-muted-foreground mb-5">
            Add your organization name so workers know who's hiring before you post an event.
          </p>
          <div className="flex flex-col gap-2">
            <Link to="/dashboard?tab=settings" className="btn-primary w-full">
              Complete profile
            </Link>
            <button onClick={() => setIncompletePopup(false)} className="btn-outline w-full">
              Not now
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (createdEvent) {
    const eventUrl = `${window.location.origin}/events/${createdEvent.id}`;
    return (
      <div className="mx-auto max-w-xl px-4 sm:px-6 py-16 text-center">
        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
        <h1 className="font-display text-3xl tracking-tighter">Your event is live</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          "{createdEvent.name}" has been created. Share the link so workers can start applying.
        </p>
        <div className="mt-6 flex justify-center">
          <ShareButtons url={eventUrl} title={createdEvent.name} text={`Check out this event on EarnMate: ${createdEvent.name}`} />
        </div>
        <button onClick={() => nav(`/manage-event/${createdEvent.id}`)} className="btn-primary mt-8 inline-flex items-center gap-2">
          Go to event <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12">
      <span className="label-eyebrow text-muted-foreground">{isEdit ? "Edit event" : "New event"}</span>
      <h1 className="mt-2 font-display text-4xl tracking-tighter">{isEdit ? "Edit Event" : "Create an Event"}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{isEdit ? "Update your event details." : "Create an event and post multiple job roles under it."}</p>

      <form onSubmit={submit} className="mt-8 space-y-5">
        <div>
          <label className="label-eyebrow text-muted-foreground">Event name <span className="text-red-500">*</span></label>
          <input className="input-base mt-2" placeholder="e.g. Rahul's Wedding, TechConf 2026" required
            value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label-eyebrow text-muted-foreground">Event date <span className="text-red-500">*</span></label>
            <input type="date" className="input-base mt-2" required
              min={new Date().toISOString().split("T")[0]}
              value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
          </div>
          <div>
            <label className="label-eyebrow text-muted-foreground">Budget (₹) <span className="text-muted-foreground text-xs">optional</span></label>
            <input type="number" className="input-base mt-2" placeholder="e.g. 200000"
              value={form.budget} onChange={e => setForm({...form, budget: e.target.value})} />
          </div>
        </div>

        <div>
          <label className="label-eyebrow text-muted-foreground">Venue name <span className="text-red-500">*</span></label>
          <input className="input-base mt-2" placeholder="e.g. Grand Ballroom, Thrissur" required
            value={form.venue} onChange={e => setForm({...form, venue: e.target.value})} />
        </div>

        <div>
          <label className="label-eyebrow text-muted-foreground">Location</label>
          <div className="mt-2">
            <LocationAutocomplete
              value={form.location}
              onChange={(val) => setForm({...form, location: val, lat: null, lng: null})}
              onSelect={({display, lat, lng}) => setForm({...form, location: display, lat, lng})}
              placeholder="Search city or area…"
            />
          </div>
          {form.lat && <p className="text-xs text-emerald-600 mt-1">✓ Location confirmed</p>}
        </div>

        <div>
          <label className="label-eyebrow text-muted-foreground">Description <span className="text-muted-foreground text-xs">optional</span></label>
          <textarea rows={3} className="input-base mt-2" placeholder="Brief description of the event…"
            value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
        </div>

        <label className="flex items-center gap-3 cursor-pointer">
          <div onClick={() => setForm(f => ({...f, is_public: !f.is_public}))}
            className={`w-10 h-6 rounded-full transition relative ${form.is_public ? "bg-brand" : "bg-slate-300"}`}>
            <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${form.is_public ? "left-5" : "left-1"}`} />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">Public event page</p>
            <p className="text-xs text-muted-foreground">Workers can see all job roles under this event</p>
          </div>
        </label>

        <button disabled={loading} className="btn-primary w-full mt-2">
          {loading ? (isEdit ? "Saving…" : "Creating…") : <span className="flex items-center justify-center gap-2">{isEdit ? "Save changes" : "Create Event"} <ArrowRight className="w-4 h-4" /></span>}
        </button>
      </form>
    </div>
  );
}
